import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  calculateBasisPoints,
  calculateNavPercentage,
  formatBasisPoints,
  formatCurrency,
  formatPercentage,
} from "../../utils/dataCalculations";
import { CardWrapper } from "./Layout";

const WinnerLoserBox = ({ title, strategies = [] }) => {
  const theme = useTheme();

  // Ensure we always show exactly 5 slots, padding with empty ones if needed
  const paddedStrategies = [...strategies];
  while (paddedStrategies?.length < 5) {
    paddedStrategies.push({ name: "", lmv: 0, totalPnl: 0, isEmpty: true });
  }
  // Only take the first 5 to ensure consistency
  const displayStrategies = paddedStrategies.slice(0, 5);

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: { xs: "14px", sm: "16px" },
          fontWeight: "700",
          mb: 2,
          color: theme.palette.text.primary,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: { xs: "11px", sm: "12px" },
          mb: 2,
          color: theme.palette.text.secondary,
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        <Typography sx={{ fontSize: "inherit", width: "40%" }}>
          Strategy
        </Typography>
        <Typography
          sx={{ fontSize: "inherit", width: "30%", textAlign: "right" }}
        >
          LMV
        </Typography>
        <Typography
          sx={{ fontSize: "inherit", width: "30%", textAlign: "right" }}
        >
          P&L
        </Typography>
      </Box>
      <Box sx={{ minHeight: "140px" }}>
        {displayStrategies.map((strategy, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: { xs: "12px", sm: "13px" },
              mb: 0.5,
              p: 1,
              borderRadius: "12px",
              height: "32px",
              alignItems: "center",
              backgroundColor: strategy.isEmpty
                ? "transparent"
                : theme.palette.background.secondary,
              border: strategy.isEmpty
                ? "none"
                : `1px solid ${theme.palette.grey[200]}`,
              transition: "all 0.2s ease",
              "&:hover": strategy.isEmpty
                ? {}
                : {
                    backgroundColor: theme.palette.background.tertiary,
                    transform: "translateX(4px)",
                  },
              opacity: strategy.isEmpty ? 0 : 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "inherit",
                width: "40%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "600",
                color: theme.palette.text.primary,
              }}
              noWrap
            >
              {strategy.isEmpty ? "" : strategy.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "inherit",
                width: "30%",
                textAlign: "right",
                color:
                  strategy.lmv > 0
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                fontWeight: "400",
              }}
            >
              {strategy.isEmpty
                ? ""
                : strategy.lmv > 0
                ? formatCurrency(strategy.lmv)
                : "-"}
            </Typography>
            <Typography
              sx={{
                fontSize: "inherit",
                width: "30%",
                textAlign: "right",
                color:
                  strategy.totalPnl < 0
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                fontWeight: "400",
              }}
            >
              {strategy.isEmpty ? "" : formatCurrency(strategy.totalPnl)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SummaryBox = ({ title, data = {} }) => {
  const theme = useTheme();

  const navPercentage = calculateNavPercentage(data.totalPnl || 0);
  const basisPoints = calculateBasisPoints(data.totalPnl || 0);

  return (
    <CardWrapper
      elevation={1}
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        borderRadius: "20px",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: { xs: "18px", sm: "20px" },
          fontWeight: "700",
          mb: 3,
          color: theme.palette.text.primary,
          position: "relative",
          zIndex: 1,
        }}
      >
        {title}
      </Typography>

      {/* P&L Section */}
      <Box sx={{ mb: 2, position: "relative", zIndex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: "600",
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "block",
            mb: 1,
          }}
        >
          Total P&L
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontWeight: "700",
            color:
              (data.totalPnl || 0) < 0
                ? theme.palette.error.main
                : theme.palette.success.main,
            mb: 1,
            lineHeight: 1.2,
          }}
        >
          {formatCurrency(data.totalPnl || 0)}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            color: theme.palette.text.secondary,
            fontWeight: "500",
          }}
        >
          {formatBasisPoints(basisPoints)} ({formatPercentage(navPercentage)} of
          NAV)
        </Typography>
      </Box>

      {/* Market Values Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 3,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              fontWeight: "600",
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "block",
              mb: 0.5,
            }}
          >
            LMV
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: "600",
              color: theme.palette.text.primary,
            }}
          >
            {formatCurrency(data.totalLmv || 0)}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              fontWeight: "600",
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "block",
              mb: 0.5,
            }}
          >
            SMV
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: "600",
              color: theme.palette.error.main,
            }}
          >
            {formatCurrency(data.totalSmv || 0)}
          </Typography>
        </Box>
      </Box>

      {/*Trading Activity Section*/}
      <Box
        sx={{
          mb: 1,
          p: 1,
          backgroundColor: "rgba(232,239,246,0.8)",
          borderRadius: "16px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "600",
            mb: 2,
            color: "#1e293b",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Trading Activity
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", color: "#059669", fontWeight: "600" }}
          >
            Buy: ${Math.round(data.buyTradeMV || 0).toLocaleString()}
          </Typography>
          <Typography
            sx={{ fontSize: "16px", color: "#dc2626", fontWeight: "600" }}
          >
            Sell: $
            {Math.round(Math.abs(data.sellTradeMV || 0)).toLocaleString()}
          </Typography>
          <Typography
            sx={{ fontSize: "16px", color: "#059669", fontWeight: "600" }}
          >
            Cover: ${Math.round(data.coverTradeMV || 0).toLocaleString()}
          </Typography>
          <Typography
            sx={{ fontSize: "16px", color: "#dc2626", fontWeight: "600" }}
          >
            Short: $
            {Math.round(Math.abs(data.shortTradeMV || 0)).toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {/* Winners and Losers */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <WinnerLoserBox title="Top Winners" strategies={data.winners || []} />
        <WinnerLoserBox title="Top Losers" strategies={data.losers || []} />
      </Box>
    </CardWrapper>
  );
};

export default SummaryBox;
